import { ResourceTypeId } from '~/client/src/shared/constants/resourceTypes'
import SortOrder from '~/client/src/shared/enums/SortOrder'

import { OWNER_FORM_DEFAULTS } from './ownerFormDefaults'
import { IResourceDefinition, IResourceFieldDefinition } from './types'

// ID and Name lead the table and stay frozen; the rest follow in this order and
// are the only columns visible by default.
const MATERIAL_FROZEN_COLUMNS = ['stxResourceId', 'name']
const MATERIAL_DEFAULT_COLUMNS = [
  ...MATERIAL_FROZEN_COLUMNS,
  'file_or_photo',
  'company',
  'responsible',
  'delivery_location',
  'install_location',
  'required_on_job_date',
  'lead_time_days',
]

// Pre-selected on a new Materials form. Name is a system field the editor always
// includes, so it isn't listed here.
const MATERIAL_DEFAULT_FORM_PROPERTIES = [
  'file_or_photo',
  'company',
  'responsible',
  'delivery_location',
  'install_location',
  'required_on_job_date',
  'lead_time_days',
]

const MATERIAL_FIELD_DEFINITIONS: Record<string, IResourceFieldDefinition> = {
  // No `table` block: card presentation only, so the property's own type keeps deciding
  // what it can do in a list.
  company: {
    dataType: 'text',
    // The company's logo when it has one; iconKey's building glyph when it does not.
    card: {
      kind: 'link',
      iconKey: 'company',
      iconKind: 'companyLogo',
      visible: true,
    },
  },
  responsible: {
    dataType: 'text',
    // The person's own avatar per the card design; iconKey's glyph when the member
    // cannot be resolved, so the icon box is never left empty.
    card: {
      kind: 'link',
      iconKey: 'user',
      iconKind: 'avatar',
      visible: true,
    },
  },
  required_on_job_date: {
    dataType: 'datetime',
    card: { kind: 'text', iconKey: 'date', visible: true },
  },
  delivery_location: {
    dataType: 'location',
    // A location link: the glyph follows the location's own type (building, zone, gate…)
    // and carries its attribute colour, per the location resource-link registry.
    card: {
      kind: 'link',
      iconKey: 'location',
      iconKind: 'location',
      visible: true,
    },
  },
  install_location: {
    dataType: 'location',
    card: {
      kind: 'link',
      iconKey: 'location',
      iconKind: 'location',
      visible: true,
    },
  },
  status: {
    dataType: 'text',
    card: { isStatus: true, visible: true },
    table: {
      groupable: true,
      filterable: true,
      searchable: true,
      labelKey: 'status',
    },
  },
  bid_package: {
    dataType: 'text',
    card: { kind: 'text', visible: true },
    table: {
      groupable: true,
      filterable: true,
      searchable: true,
      labelKey: 'bid_package',
    },
  },
  assembly: {
    dataType: 'text',
    card: { kind: 'text', visible: true },
    table: {
      groupable: true,
      filterable: true,
      searchable: true,
      labelKey: 'assembly',
    },
  },
}

export const materialResourceDefinition: IResourceDefinition = {
  resourceTypeId: ResourceTypeId.MATERIALS,
  fields: MATERIAL_FIELD_DEFINITIONS,
  valueSources: {
    stxResourceId: { source: 'root' },
  },
  card: {
    titleKey: 'name',
    subtitleKey: 'stxResourceId',
    // The card's meta rows, in order. status is absent on purpose: it is isStatus, so it
    // renders as the badge rather than a row.
    defaultDisplayPropertyKeys: [
      'company',
      'responsible',
      'required_on_job_date',
      'delivery_location',
      'install_location',
    ],
  },
  table: {
    columnOrder: MATERIAL_DEFAULT_COLUMNS,
    defaultVisibleKeys: MATERIAL_DEFAULT_COLUMNS,
    defaultFrozenKeys: MATERIAL_FROZEN_COLUMNS,
  },
  form: {
    defaultSelectedKeys: MATERIAL_DEFAULT_FORM_PROPERTIES,
    ...OWNER_FORM_DEFAULTS,
  },
  ui: {
    noneGroupingKey: 'none',
    defaultGroupingKey: null,
    defaultSortKey: 'name',
    defaultSortOrder: SortOrder.ASC,
  },
}
